import React from "react";
import { motion } from "framer-motion";
import { getTranslatedProvince, getTranslatedRegion, getTranslatedParty } from "@/utils/translations";
import { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { moroccoRegions } from "@/data/parties";
import { Facebook, Instagram, Users, Search, MessageCircle, MapPin, CheckCircle2, ArrowRight, ArrowLeft, Vote, SearchIcon, CalendarDays, Info } from "lucide-react";
import { XIcon } from "@/components/icons/XIcon";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/i18n/I18nProvider";
import { allPartiesIncludingNonParliamentary } from "@/data/parties";
import { useAuth } from "@/contexts/AuthContext";
import { isGlobalDemoModeEnabled } from "@/data/demoStore";
import { getSectionContent } from "@/data/cmsStore";
import CandidateContactDialog from "@/components/CandidateContactDialog";
import { useToast } from "@/hooks/use-toast";
import { fetchPublicCandidates } from "@/data/candidatesStore";

const CandidatesResultPage = () => {
  const { t, isRtl } = useI18n();
  const BackArrow = isRtl ? ArrowRight : ArrowLeft;
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const { lang } = useI18n();
  const cleanMatch = (a: string, b: string) => {
    if (!a || !b) return false;
    const norm = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[\u2019’]/g, "'").toLowerCase().trim();
    return norm(a) === norm(b);
  };

  const normalizeString = (s: string) => {
    if (!s) return "";
    return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[\u2019’]/g, "'").toLowerCase().trim();
  };

  const getTranslatedCategory = (cat: string, lang: string) => {
      if (cat === "Autre") {
          if (lang === 'ar') return "أخرى";
          if (lang === 'zgh') return "ⵢⴰⴹⵏ";
      }
      return cat;
  };

  const getCategoryLabel = (lang: string) => {
      if (lang === 'ar') return "الفئة :";
      if (lang === 'zgh') return "ⵜⴰⵙⵎⵉⵍⵜ :";
      return "Catégorie :";
  };

  
  const urlRegion = searchParams.get("region");
  const urlProvince = searchParams.get("province");
  const urlType = searchParams.get("type");
  const urlParty = searchParams.get("party");
  const urlDataType = searchParams.get("dataType");
  
  const [filterParty, setFilterParty] = useState(urlParty || "all");
  const [filterRegion, setFilterRegion] = useState(urlRegion || "all");
  const [filterProvince, setFilterProvince] = useState(urlProvince || "all");
  const [filterType, setFilterType] = useState(urlType || "all");

  const provincesForDropdown = useMemo(() => {
    if (filterRegion && filterRegion !== "all") {
      return moroccoRegions.find(r => r.name === filterRegion || r.nameAr === filterRegion)?.provinces || [];
    }
    return moroccoRegions.flatMap(r => r.provinces);
  }, [filterRegion]);

  const isGlobalSearch = (!filterParty || filterParty === "all") && (!filterRegion || filterRegion === "all") && (!filterProvince || filterProvince === "all");

  const [isDemo, setIsDemo] = useState(isGlobalDemoModeEnabled());
  const [contactCandidate, setContactCandidate] = useState<any>(null);
  const { toast } = useToast();
  const [cms, setCms] = useState(() => getSectionContent("hero", "home"));
  
  useEffect(() => {
    const handleCmsChange = () => setCms(getSectionContent("hero", "home"));
    const handleDemoChange = (e: any) => setIsDemo(e.detail.enabled);
    
    window.addEventListener("demoModeChanged", handleDemoChange);
    window.addEventListener("cmsLayoutChanged", handleCmsChange);
    window.addEventListener("cmsLayoutDraftChanged", handleCmsChange);
    window.addEventListener("stagingModeChanged", handleCmsChange);
    
    return () => {
      window.removeEventListener("demoModeChanged", handleDemoChange);
      window.removeEventListener("cmsLayoutChanged", handleCmsChange);
      window.removeEventListener("cmsLayoutDraftChanged", handleCmsChange);
      window.removeEventListener("stagingModeChanged", handleCmsChange);
    };
  }, []);

  const [publicCandidates, setPublicCandidates] = useState<any[]>([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(true);

  useEffect(() => {
    fetchPublicCandidates().then(data => {
        const mapped = data.map(c => ({
          ...c,
          prenomCandidat: c.firstName,
          nomCandidat: c.lastName,
          prenomCandidatAr: c.firstNameAr,
          nomCandidatAr: c.lastNameAr,
        }));
        setPublicCandidates(mapped);
        setIsLoadingCandidates(false);
      });
    // Removed import closing
  }, []);

  const realCandidates = useMemo(() => {
    return publicCandidates;
  }, [publicCandidates]);

  const candidateMatchesSearch = (c: any) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const fullName = `${c.prenomCandidat} ${c.nomCandidat}`.toLowerCase();
    const fullNameAr = `${c.prenomCandidatAr} ${c.nomCandidatAr}`.toLowerCase();
    const provinceMatch = (c.province || "").toLowerCase().includes(q);
    const regionMatch = (c.region || "").toLowerCase().includes(q);
    const typeMatchLocale = c.listType === "locale" && (isRtl ? "لائحة محلية" : "liste locale").includes(q);
    const typeMatchRegionale = c.listType === "regionale" && (isRtl ? "لائحة جهوية" : "liste régionale").includes(q);
    
    return fullName.includes(q) || fullNameAr.includes(q) || provinceMatch || regionMatch || typeMatchLocale || typeMatchRegionale;
  };

  const activeRegionsToRender = useMemo(() => {
    let result = moroccoRegions;
    if (filterRegion && filterRegion !== "all") {
      const normFilter = normalizeString(filterRegion);
      result = result.filter(r => normalizeString(r.name) === normFilter || normalizeString(r.nameAr) === normFilter);
    }
    
    return result.map(region => {
       let validProvinces = region.provinces;
       if (filterProvince && filterProvince !== "all") {
         const normProv = normalizeString(filterProvince);
         validProvinces = validProvinces.filter(p => normalizeString(p) === normProv);
       }
       
       const localeActive = filterType === "all" || normalizeString(filterType) === "locale" || normalizeString(filterType) === "locallist";
       const regionalActive = filterType === "all" || normalizeString(filterType) === "regionale" || normalizeString(filterType) === "regionallist";

       const regionalMatchesSearch = (!searchQuery || region.name.toLowerCase().includes(searchQuery.toLowerCase()) || (isRtl ? "لائحة جهوية" : "liste régionale").includes(searchQuery.toLowerCase()));

       return {
         ...region,
         filteredProvinces: localeActive ? validProvinces : [],
         showRegionalList: regionalActive && regionalMatchesSearch && (filterProvince === "all" || !filterProvince)
       }
    }).filter(r => {
      const hasRealLocale = realCandidates.some(c => cleanMatch(c.region, r.name) && c.listType === "locale" && r.filteredProvinces.some(p => cleanMatch(p, c.province || "")) && (filterParty === "all" || cleanMatch(c.partyAcronym, filterParty)) && candidateMatchesSearch(c));
      const hasRealRegionale = r.showRegionalList && realCandidates.some(c => cleanMatch(c.region, r.name) && c.listType === "regionale" && (filterParty === "all" || cleanMatch(c.partyAcronym, filterParty)) && candidateMatchesSearch(c));
      return hasRealLocale || hasRealRegionale;
    });
  }, [filterRegion, filterProvince, filterType, searchQuery, isRtl, realCandidates, filterParty]);

  const [pageTitle, setPageTitle] = useState(isRtl ? "نتائج البحث عن المرشحين" : "Résultats de la recherche des candidats");

  useEffect(() => {
    let title = isRtl ? "نتائج البحث عن المرشحين" : "Résultats de la recherche des candidats";
    if (filterParty && filterParty !== "all") {
      title += ` - ${getTranslatedParty(filterParty, lang)}`;
    }
    if (filterRegion && filterRegion !== "all") {
      title += isRtl ? ` بجهة ${getTranslatedRegion(filterRegion, lang)}` : ` dans la région de ${filterRegion}`;
    }
    setPageTitle(title);
  }, [filterParty, filterRegion, isRtl, lang]);

  return (
    <div className="min-h-screen bg-background flex flex-col pt-16">
      <Navbar />

      <main className="flex-grow pt-12 pb-24 border-b border-border">
        <div className="container max-w-6xl">
          <Button variant="ghost" className="mb-6 font-body -ml-4 hover:bg-transparent hover:text-primary transition-colors text-muted-foreground" asChild>
            <Link to="/">
              <BackArrow className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" />
              {isRtl ? "العودة إلى البحث مجددا" : "Retour à la recherche interactive"}
            </Link>
          </Button>

          <motion.div
            className="mb-12 bg-card/95 backdrop-blur-md rounded-3xl border border-border shadow-lg overflow-hidden relative"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-accent to-primary" />
            <div className="p-6 md:p-8">
              
              {urlDataType === "results" ? (
                <div className="py-12 px-4 text-center flex flex-col items-center">
                  <div className="w-24 h-24 bg-accent/10 rounded-full flex items-center justify-center mb-6">
                    <CalendarDays className="w-12 h-12 text-accent" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-6">
                    {isRtl ? "نتائج الانتخابات قريباً!" : "Les résultats arrivent à grands pas !"}
                  </h2>
                  <div className="bg-muted/40 border border-border rounded-2xl p-6 max-w-2xl">
                    <p className="text-foreground font-body text-lg leading-relaxed mb-2">
                       {isRtl 
                          ? "سيتم الإعلان عن النتائج الرسمية والمفصلة للانتخابات البرلمانية مباشرة بعد يوم الاقتراع."
                          : "Les résultats officiels détaillés des élections parlementaires seront communiqués ici même au lendemain du scrutin national."}
                    </p>
                    <p className="text-primary font-bold text-xl mb-4 font-display">
                       {isRtl ? "المرجو العودة ابتداءً من 23 سبتمبر 2026" : "Revenez le 23 septembre 2026 !"}
                    </p>
                    <p className="text-muted-foreground text-sm">
                       {isRtl ? "شكراً على التزامكم الديمقراطي والمشاركة الفعالة." : "Nous vous remercions de votre participation démocratique."}
                    </p>
                  </div>
                  <Button asChild size="lg" className="rounded-full px-8 mt-8 shadow-md">
                    <Link to="/">
                      <BackArrow className="w-4 h-4 mr-2 rtl:hidden" />
                      <BackArrow className="w-4 h-4 ml-2 hidden rtl:inline-block" />
                      {isRtl ? "العودة إلى الصفحة الرئيسية" : "Retour à l'accueil"}
                    </Link>
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 border-b border-border pb-4">
                    <h2 className="font-display text-2xl md:text-3xl flex items-center gap-2 font-bold text-foreground leading-tight max-w-3xl">
                      <Users className="w-8 h-8 text-primary shrink-0" />
                      {pageTitle}
                    </h2>
                    <Button variant="outline" asChild className="hidden text-primary border-primary/30 hover:bg-primary/5 hover:text-primary">
                      <Link to="/">
                        <Search className="w-4 h-4 mr-2" /> 
                        {isRtl ? "إجراء بحث آخر" : "Nouvelle recherche"}
                      </Link>
                    </Button>
                  </div>
                  
                  <div className="flex flex-col gap-2 mb-6 border-b border-border/50 pb-4">
                    <div className="flex gap-2 flex-wrap items-center">
                      <span className="text-muted-foreground font-body font-semibold">{isRtl ? "الفلاتر النشطة:" : "Filtres actifs:"}</span>
                      {filterParty && filterParty !== "all" && <Badge variant="outline" className="bg-background border-primary/40">{getTranslatedParty(filterParty, lang)}</Badge>}
                      {filterRegion && filterRegion !== "all" && <Badge variant="outline" className="bg-background border-primary/40">{getTranslatedRegion(filterRegion, lang)}</Badge>}
                      {filterProvince && filterProvince !== "all" && <Badge variant="outline" className="bg-background border-primary/40">{getTranslatedProvince(filterProvince, lang)}</Badge>}
                      {filterType && filterType !== "all" && <Badge variant="outline" className="bg-background border-primary/40">{filterType.toLowerCase() === 'locale' ? (isRtl ? 'لائحة محلية' : 'Liste locale') : filterType.toLowerCase() === 'regionale' ? (isRtl ? 'لائحة جهوية' : 'Liste régionale') : filterType}</Badge>}
                      
                      {isGlobalSearch && (
                        <div className="text-primary text-sm flex items-center gap-1.5 font-medium ml-2">
                          <Info className="w-4 h-4" />
                          {isRtl ? "للحصول على نتيجة مثالية، يرجى تضييق نطاق بحثك." : "Pour un résultat optimal, merci d'affiner votre recherche."}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 mb-8 bg-muted/10 p-4 rounded-xl border border-border">
                    <div className="flex gap-2 flex-wrap flex-1 w-full">
                      <Select onValueChange={setFilterParty} value={filterParty}>
                        <SelectTrigger className="w-full md:w-auto min-w-[140px] bg-background">
                          <SelectValue placeholder={isRtl ? "الحزب السياسي" : "Parti politique"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{isRtl ? "جميع الأحزاب" : "Tous les partis"}</SelectItem>
                          {allPartiesIncludingNonParliamentary.map(p => (
                            <SelectItem key={p.acronym} value={p.acronym}>{getTranslatedParty(p.acronym, lang)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Select onValueChange={(val) => { setFilterRegion(val); setFilterProvince("all"); }} value={filterRegion}>
                        <SelectTrigger className="w-full md:w-auto min-w-[150px] bg-background border-primary/20 hover:border-primary/50">
                          <SelectValue placeholder={isRtl ? "الجهة" : "Région"} />
                        </SelectTrigger>
                        <SelectContent className="max-h-[35vh]">
                          <SelectItem value="all">{isRtl ? "جميع الجهات" : "Toutes les régions"}</SelectItem>
                          {moroccoRegions.map(r => (
                            <SelectItem key={r.name} value={r.name}>{isRtl && r.nameAr ? r.nameAr : r.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Select onValueChange={setFilterProvince} value={filterProvince} disabled={!filterRegion || filterRegion === 'all'}>
                        <SelectTrigger className="w-full md:w-auto min-w-[160px] bg-background border-primary/20 hover:border-primary/50">
                          <SelectValue placeholder={isRtl ? "العمالة / الإقليم" : "Préfecture / Province"} />
                        </SelectTrigger>
                        <SelectContent className="max-h-[35vh]">
                          <SelectItem value="all">{isRtl ? "جميع العمالات" : "Toutes les préfectures"}</SelectItem>
                          {provincesForDropdown.map(p => (
                            <SelectItem key={p} value={p}>{getTranslatedProvince(p, lang)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Select onValueChange={setFilterType} value={filterType}>
                        <SelectTrigger className="w-full md:w-auto min-w-[140px] bg-background border-primary/20 hover:border-primary/50">
                          <SelectValue placeholder={isRtl ? "نوع اللائحة" : "Type de liste"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{isRtl ? "جميع الأنواع" : "Tous les types"}</SelectItem>
                          <SelectItem value="locale">{isRtl ? "لائحة محلية" : "Liste locale"}</SelectItem>
                          <SelectItem value="regionale">{isRtl ? "لائحة جهوية" : "Liste régionale"}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="relative w-full lg:w-64 mt-2 lg:mt-0 shrink-0">
                      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                        <SearchIcon className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <input 
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={isRtl ? "بحث..." : "Recherche Rapide..."}
                        className="w-full bg-background border border-border rounded-xl pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>
                  </div>

                  {isLoadingCandidates && (
                    <div className="flex justify-center my-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                    </div>
                  )}

                  {!isLoadingCandidates && (
                  <div className="space-y-8 max-h-[60vh] overflow-y-auto custom-scrollbar pr-2 bg-muted/5 p-4 md:p-6 rounded-2xl border border-border/50 relative">
                    {activeRegionsToRender.length > 0 ? (
                      activeRegionsToRender.map((regionGroup, groupIndex) => (
                        <div key={groupIndex} className="bg-background/50 rounded-2xl p-4 md:p-6 border border-border/60 shadow-sm relative overflow-hidden">
                           <div className="absolute left-0 top-0 w-1.5 h-full bg-primary/40 rounded-l-2xl"></div>
                           
                           <div className="mb-6 pb-3 border-b border-border/80 flex items-center gap-3">
                              <div className="bg-primary/10 p-2 rounded-lg text-primary">
                                <MapPin className="w-5 h-5" />
                              </div>
                              <h3 className="text-xl md:text-2xl font-display font-semibold text-foreground">
                                {isRtl && regionGroup.nameAr ? regionGroup.nameAr : regionGroup.name}
                              </h3>
                           </div>

                           <div className="space-y-4">
                             {/* Rendu des vrais candidats pour cette région (Listes Locales) */}
                             {realCandidates
                               .filter(c => cleanMatch(c.region, regionGroup.name) && c.listType === "locale" && regionGroup.filteredProvinces.some(p => cleanMatch(p, c.province || "")) && (filterParty === "all" || cleanMatch(c.partyAcronym, filterParty)) && candidateMatchesSearch(c))
                               .map(c => (
                               <div key={`real_${c.partyAcronym}_${c.region}_${c.province}_${c.prenomCandidat}`} className="flex flex-col sm:flex-row gap-4 bg-background p-4 rounded-xl border border-border hover:border-primary/50 hover:shadow-md transition-all group">
                                 <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-2xl bg-muted overflow-hidden shrink-0 shadow-inner flex items-center justify-center relative">
                                   {c.photoUrl ? (
                                     <img src={c.photoUrl} alt="Candidat" className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                                   ) : (
                                     <img 
                                       src={`https://ui-avatars.com/api/?name=${encodeURIComponent(c.prenomCandidat + ' ' + c.nomCandidat)}&background=random&size=150`} 
                                       alt="Candidat"
                                       className="w-full h-full object-cover mix-blend-multiply transition-transform group-hover:scale-105"
                                     />
                                   )}
                                 </div>
                                 <div className="flex-1 min-w-0 flex flex-col justify-center">
                                   <div className="flex justify-between items-start">
                                     <Badge className="w-fit mb-2 bg-primary/10 text-primary border-primary/20 text-[10px] font-bold px-2 py-0.5 shadow-sm">
                                       {getTranslatedParty(c.partyAcronym, lang)} - {isRtl ? "لائحة محلية" : "Liste Locale"}
                                     </Badge>
                                     <div className="flex gap-2 relative z-10">
                                       {c.whatsapp && (
                                         <a href={`https://wa.me/${c.whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noopener noreferrer" className="text-[#25D366] hover:scale-110 transition-transform p-1.5 bg-green-50 rounded-full" title="Contacter sur WhatsApp">
                                           <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                                         </a>
                                       )}
                                       {c.email && (
                                         <Button onClick={() => setContactCandidate({ id: `c_${c.partyAcronym}_${c.region}_${c.province}_${c.prenomCandidat}`, firstName: c.prenomCandidat, lastName: c.nomCandidat, partyAcronym: c.partyAcronym, region: c.region, province: c.province })} variant="outline" size="sm" className="h-7 text-[10px] px-2.5 py-0 border-primary/30 text-primary hover:bg-primary/10 gap-1.5 rounded-full cursor-pointer z-20">
                                           <MessageCircle className="w-3.5 h-3.5" /> {isRtl ? "تواصل" : "Contact"}
                                         </Button>
                                       )}
                                     </div>
                                   </div>
                                   <h5 className="font-display font-bold text-lg text-foreground mb-1 group-hover:text-primary transition-colors">
                                      {lang === 'ar' ? (
                                        <>{c.prenomCandidatAr || c.prenomCandidat} {c.nomCandidatAr || c.nomCandidat}</>
                                      ) : (
                                        <>{c.prenomCandidat || c.prenomCandidatAr} {c.nomCandidat || c.nomCandidatAr}</>
                                      )}
                                   </h5>
                                   <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-body mb-2">
                                     <MapPin className="w-3.5 h-3.5 text-primary/60" /> {getTranslatedProvince(c.province, lang)}
                                   </div>
                                   {c.categorieSociopro && (
                                     <p className="font-body text-xs text-muted-foreground leading-relaxed line-clamp-2">
                                       {getCategoryLabel(lang)} {getTranslatedCategory(c.categorieSociopro, lang)}
                                     </p>
                                   )}
                                 </div>
                               </div>
                             ))}

                             {/* Rendu des vrais candidats pour cette région (Listes Régionales) */}
                             {regionGroup.showRegionalList && realCandidates
                               .filter(c => cleanMatch(c.region, regionGroup.name) && c.listType === "regionale" && (filterParty === "all" || cleanMatch(c.partyAcronym, filterParty)) && candidateMatchesSearch(c))
                               .map(c => (
                               <div key={`real_${c.partyAcronym}_${c.region}_regionale_${c.prenomCandidat}`} className="flex flex-col sm:flex-row gap-4 bg-background p-4 rounded-xl border border-border hover:border-accent/50 hover:shadow-md transition-all group mt-2 relative overflow-hidden">
                                 <div className="absolute right-0 top-0 w-1 h-full bg-accent/40 rounded-r-xl"></div>
                                 <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-2xl bg-muted overflow-hidden shrink-0 shadow-inner flex items-center justify-center relative">
                                   {c.photoUrl ? (
                                     <img src={c.photoUrl} alt="Candidat" className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                                   ) : (
                                     <img 
                                       src={`https://ui-avatars.com/api/?name=${encodeURIComponent(c.prenomCandidat + ' ' + c.nomCandidat)}&background=random&size=150`} 
                                       alt="Candidat Régional"
                                       className="w-full h-full object-cover mix-blend-multiply transition-transform group-hover:scale-105"
                                     />
                                   )}
                                 </div>
                                 <div className="flex-1 min-w-0 flex flex-col justify-center">
                                 <div className="flex justify-between items-start">
                                   <Badge className="w-fit mb-2 bg-accent/10 text-accent border-accent/20 text-[10px] font-bold px-3 py-0.5 shadow-sm">
                                     {getTranslatedParty(c.partyAcronym, lang)} - {isRtl ? "لائحة جهوية" : "Liste Régionale"}
                                   </Badge>
                                   <div className="flex gap-2 relative z-10">
                                     {c.whatsapp && (
                                       <a href={`https://wa.me/${c.whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noopener noreferrer" className="text-[#25D366] hover:scale-110 transition-transform p-1.5 bg-green-50 rounded-full" title="Contacter sur WhatsApp">
                                         <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
                                       </a>
                                     )}
                                     {c.email && (
                                       <Button onClick={() => setContactCandidate({ id: `c_${c.partyAcronym}_${c.region}_regionale_${c.prenomCandidat}`, firstName: c.prenomCandidat, lastName: c.nomCandidat, partyAcronym: c.partyAcronym, region: c.region, province: "" })} variant="outline" size="sm" className="h-7 text-[10px] px-2.5 py-0 border-accent/30 text-accent hover:bg-accent/10 gap-1.5 rounded-full cursor-pointer z-20">
                                         <MessageCircle className="w-3.5 h-3.5" /> {isRtl ? "تواصل" : "Contact"}
                                       </Button>
                                     )}
                                   </div>
                                 </div>
                                 <h5 className="font-display font-bold text-lg text-foreground mb-1 group-hover:text-accent transition-colors">
                                   {lang === 'ar' ? (
                                     <>{c.prenomCandidatAr || c.prenomCandidat} {c.nomCandidatAr || c.nomCandidat}</>
                                   ) : (
                                     <>{c.prenomCandidat || c.prenomCandidatAr} {c.nomCandidat || c.nomCandidatAr}</>
                                   )}
                                 </h5>
                                 <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-body mb-2">
                                   <MapPin className="w-3.5 h-3.5 text-accent/60" /> {getTranslatedRegion(c.region, lang)}{c.province ? ` - ${getTranslatedProvince(c.province, lang)}` : ''}
                                 </div>
                                 {c.categorieSociopro && (
                                     <p className="font-body text-xs text-muted-foreground leading-relaxed line-clamp-2">
                                       {getCategoryLabel(lang)} {getTranslatedCategory(c.categorieSociopro, lang)}
                                     </p>
                                 )}
                                 </div>
                               </div>
                             ))}

                           </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-12 text-center flex flex-col items-center border border-dashed rounded-xl border-border bg-background/50">
                        <Users className="w-12 h-12 text-muted-foreground/50 mb-4" />
                        <p className="text-muted-foreground font-display font-bold text-xl">
                          {lang === 'ar' ? "في انتظار التجميع" : lang === 'zgh' ? "ⴰⵔ ⵉⵜⵜⵓⵔⵊⴰ ⴰⵙⵎⵓⵏ" : "En attente de consolidation"}
                        </p>
                        <p className="text-muted-foreground font-body text-sm mt-4">
                          {isRtl ? "لم يتم إدخال أي مرشحين لهذه المعايير بعد." : "Aucun candidat n'a encore été saisi pour ces critères."}
                        </p>
                        <Button variant="link" onClick={() => {setFilterParty("all"); setFilterRegion("all"); setFilterProvince("all"); setFilterType("all"); setSearchQuery("");}} className="mt-2">
                           {isRtl ? "إعادة ضبط الفلاتر" : "Réinitialiser les filtres"}
                        </Button>
                      </div>
                    )}
                  </div>
                  )}
                </>
              )}
            </div>
          </motion.div>

          {/* Voter Verification Banner (Moved to bottom) */}
          <Link to="/voter" className="block mt-12 hover:scale-[1.01] transition-transform">
            <motion.div 
              className="bg-accent/10 border border-accent/20 rounded-2xl p-6 flex flex-col md:flex-row items-center gap-6 shadow-sm overflow-hidden relative group"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 rounded-full blur-2xl -mr-16 -mt-16 transition-all group-hover:bg-accent/10"></div>
              
              <div className="w-16 h-16 rounded-full bg-accent text-accent-foreground flex items-center justify-center shrink-0 shadow-md">
                <Vote className="w-8 h-8" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="font-display font-bold text-xl md:text-2xl text-foreground mb-2 flex items-center justify-center md:justify-start gap-2">
                  {isRtl ? "تحقق من تسجيلك الانتخابي" : "Vérifiez votre inscription électorale"}
                  <CheckCircle2 className="w-5 h-5 text-accent" />
                </h3>
                <p className="text-muted-foreground text-sm md:text-base font-body leading-relaxed">
                  {isRtl 
                    ? "تأكد من أنك مسجل في اللوائح الانتخابية وابحث عن مكتب التصويت الخاص بك بخطوات بسيطة." 
                    : "Assurez-vous que vous êtes inscrit(e) sur les listes électorales et trouvez votre bureau de vote en quelques clics."}
                </p>
              </div>
              <div className="shrink-0 flex items-center hidden md:flex">
                <div className="w-10 h-10 rounded-full bg-background border border-border flex items-center justify-center shadow-sm group-hover:border-accent group-hover:text-accent transition-colors">
                  <ArrowRight className="w-5 h-5 rtl:hidden" />
                  <ArrowLeft className="w-5 h-5 hidden rtl:block" />
                </div>
              </div>
            </motion.div>
          </Link>
        </div>
      </main>

      <Footer />
      {/* Dialogs */}
      {contactCandidate && (
        <CandidateContactDialog
          isOpen={!!contactCandidate}
          onClose={() => setContactCandidate(null)}
          candidate={contactCandidate}
          onSuccess={() => {
            toast({ title: "Message envoyé", description: "Votre message a été envoyé au candidat avec succès." });
          }}
        />
      )}
    </div>
  );
};

export default CandidatesResultPage;
